import { ICountry, IProvince } from "redux/reducers/models/customer";
import { TProductVendor, TVendorContact } from "redux/reducers/models/product";

export type VendorFormState = {
  title: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  provinceId: number | null;
  provinceText: string;
  postalCode: string;
  countryId: number | null;
  countryText: string;
  notes: string;
  contacts: TVendorContact[];
};

export type VendorWrite = {
  title: string;
  address_line: string;
  address_line_2: string;
  city: string;
  province: string;
  country: string;
  postal_code: string;
  notes: string;
  contacts: TVendorContact[];
};

export function blankContact(): TVendorContact {
  return { name: "", role: "", email: "", phone: "" };
}

export function blankVendorForm(): VendorFormState {
  return {
    title: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    provinceId: null,
    provinceText: "",
    postalCode: "",
    countryId: null,
    countryText: "",
    notes: "",
    contacts: [blankContact()],
  };
}

function asContact(contact?: Partial<TVendorContact> | null): TVendorContact {
  return {
    name: contact?.name ?? "",
    role: contact?.role ?? "",
    email: contact?.email ?? "",
    phone: contact?.phone ?? "",
  };
}

export function vendorContacts(vendor: Partial<TProductVendor> | null | undefined): TVendorContact[] {
  if (!vendor) {
    return [];
  }
  if (Array.isArray(vendor.contacts)) {
    return vendor.contacts.map(contact => asContact(contact));
  }
  if (vendor.contact_name || vendor.email || vendor.phone) {
    return [asContact({ name: vendor.contact_name, email: vendor.email, phone: vendor.phone })];
  }
  return [];
}

export function vendorLocation(vendor: Partial<TProductVendor>): string {
  return [vendor.city, vendor.province].filter(Boolean).join(", ");
}

function samePlace(stored: string | null | undefined, name?: string, code?: string): boolean {
  const value = (stored ?? "").trim().toLowerCase();
  if (!value) {
    return false;
  }
  return value === (name ?? "").trim().toLowerCase() || value === (code ?? "").trim().toLowerCase();
}

export function matchCountry(countries: ICountry[], stored: string | null | undefined): ICountry | undefined {
  return countries.find(country => samePlace(stored, country.name, country.code));
}

export function matchProvince(country: ICountry | undefined, stored: string | null | undefined): IProvince | undefined {
  return country?.provinces?.find(province => samePlace(stored, province.name, province.code));
}

/** Map a saved country and province string onto the country list. Unknown values stay as text. */
export function withCountrySelection(form: VendorFormState, countries: ICountry[]): VendorFormState {
  const country = matchCountry(countries, form.countryText);
  const province = matchProvince(country, form.provinceText);
  return {
    ...form,
    countryId: country?.id ?? null,
    provinceId: province?.id ?? null,
    countryText: country?.name ?? form.countryText,
    provinceText: province?.name ?? form.provinceText,
  };
}

export function formFromVendor(vendor: TProductVendor): VendorFormState {
  const contacts = vendorContacts(vendor);

  return {
    title: vendor.title ?? "",
    addressLine1: vendor.address_line ?? "",
    addressLine2: vendor.address_line_2 ?? "",
    city: vendor.city ?? "",
    provinceId: null,
    provinceText: vendor.province ?? "",
    postalCode: vendor.postal_code ?? "",
    countryId: null,
    countryText: vendor.country ?? "",
    notes: vendor.notes ?? "",
    contacts: contacts.length ? contacts : [blankContact()],
  };
}

export function vendorPayload(form: VendorFormState): VendorWrite {
  return {
    title: form.title.trim(),
    address_line: form.addressLine1.trim(),
    address_line_2: form.addressLine2.trim(),
    city: form.city.trim(),
    province: form.provinceText.trim(),
    country: form.countryText.trim(),
    postal_code: form.postalCode.trim(),
    notes: form.notes.trim(),
    contacts: form.contacts.map(contact => ({
      name: contact.name.trim(),
      role: contact.role.trim(),
      email: contact.email.trim(),
      phone: contact.phone.trim(),
    })),
  };
}

export function snapshotVendorForm(form: VendorFormState): string {
  return JSON.stringify({
    title: form.title,
    addressLine1: form.addressLine1,
    addressLine2: form.addressLine2,
    city: form.city,
    provinceId: form.provinceId,
    provinceText: form.provinceText,
    postalCode: form.postalCode,
    countryId: form.countryId,
    countryText: form.countryText,
    notes: form.notes,
    contacts: form.contacts.map(contact => ({
      name: contact.name,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
    })),
  });
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function createdVendorId(data: unknown): number | undefined {
  if (!data || typeof data !== "object") {
    return undefined;
  }
  const record = data as { id?: number; data?: { id?: number } };
  if (typeof record.id === "number") {
    return record.id;
  }
  if (typeof record.data?.id === "number") {
    return record.data.id;
  }
  return undefined;
}
